import React, { useEffect, useRef } from "react";
import { Box, Grid, Paper, Typography } from "@mui/material";

import ResultsSidebar from "./ResultsSidebar";
import ResultsHeader from "./ResultsHeader";
import ResultsStepSummary from "./steps/ResultsStepSummary";
import ResultsStepPlan from "./steps/ResultsStepPlan";
import ResultsMealSelection from "./steps/ResultsMealSelection";
import ResultsTreatSelection from "./steps/ResultsTreatSelection";
import ResultsReview from "./steps/ResultsReview";
import ProfileSummaryScreen from "./steps/ProfileSummaryScreen";
import { ResultsFooter } from "./ResultsFooter";
import { TurnstileProvider } from "./shared/turnstile";
import { QuizShell } from "@/components/quiz/shared/QuizShell";
import { quizAsset } from "@/components/quiz/shared/quizAssets";
import { NinetyFourBadge } from "./review/ReviewSections";
import { useQuiz } from "@/state/useQuiz";
import { useQuizContext } from "@/state/QuizContext";
import { useResultsStep } from "@/state/useResultsStep";
import { writeResultsStepToURL, writeQuizStepToURL } from "@/state/urlState";
import { useDog } from "@/state/useDog";
import { Allergy } from "@/helpers/enums";
import { GTM } from "@/services/gtm";

const LOGO_SRC =
    "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Logo.png?v=1756962889";

const WAVE_SRC =
    "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/Intersect.svg?v=1763908060";

// Keep these out of the component so they aren't re-created every render
const RESULTS_STEPS: Record<number, React.ComponentType<any>> = {
    1: ResultsStepSummary,
    2: ResultsStepPlan,
    3: ResultsMealSelection,
    4: ResultsTreatSelection,
    5: ResultsReview,
};

const RESULTS_STEP_META: Record<
    number,
    { title: string; subtitle?: (dogCount: number) => string }
> = {
    1: {
        title: `"Your Dog’s Profile"`,
        subtitle: (dogCount: number) =>
            `You have ${dogCount} dog${dogCount > 1 ? "s" : ""} in this plan.`,
    },
    2: {
        title: "Select Your Plan",
        subtitle: (dogCount: number) =>
            `Recommended subscription plan for your dog${dogCount > 1 ? "s" : ""}.`,
    },
    3: {
        title: "Customise Meals",
        subtitle: () => `Choose meals for each dog.`,
    },
    4: {
        title: "Add Treats",
        subtitle: () => "Optionally include treats to delight your dog.",
    },
    5: {
        title: "Review & Confirm",
        subtitle: () => "Double-check everything before checkout.",
    },
};

// Simple warning card for extreme allergies
function AllergyWarningCard({ name }: { name?: string }) {
    return (
        <Paper
            elevation={5}
            sx={{
                maxWidth: 700,
                width: "100%",
                p: { xs: 3, md: 5 },
                borderRadius: "24px",
                backgroundColor: "#FFF3F2",
                border: "2px solid #F5B0AA",
                textAlign: "center",
                mx: "auto",
            }}
        >
            <Typography
                sx={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: "#B02A1A",
                    mb: 2,
                    fontFamily: "Prompt",
                }}
            >
                We’re sorry, {name || "pup"} 🐾
            </Typography>

            <Typography
                sx={{
                    fontSize: 16,
                    color: "#7A3126",
                    maxWidth: 520,
                    mx: "auto",
                    lineHeight: 1.6,
                }}
            >
                With all of these allergies selected, we don’t yet have any recipes that
                safely match your dog’s needs.
                <br />
                <br />
                If this was selected by mistake, you can use the{" "}
                <strong>buttons at the bottom</strong> to go back and adjust the
                allergy options on the treats step.
                <br />
                <br />
                We’re working on expanding our allergy-friendly range — thank you for
                your patience. 💚
            </Typography>
        </Paper>
    );
}

export default function ResultsStepper() {
    const { dogs, activeDogId } = useQuiz();
    const { dispatch } = useQuizContext();
    const { resultsStep, setResultsStep } = useResultsStep();

    // 1. Normalise the step so it always lives in [1, 5]
    const safeStep =
        resultsStep && resultsStep >= 1 && resultsStep <= 5 ? resultsStep : 1;

    useEffect(() => {
        if (resultsStep !== safeStep) {
            // Normalise state if it ever drifts out of bounds (0, 7, etc.)
            setResultsStep(safeStep);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultsStep, safeStep]);

    // 2. Keep URL in sync whenever the (safe) step changes
    useEffect(() => {
        writeResultsStepToURL(safeStep);
    }, [safeStep]);

    // 3. Track results page viewed (on mount)
    useEffect(() => {
        const totalDailyGrams = dogs.reduce((sum, dog) => {
            const dailyGrams = Number(dog.form?.dailyGrams) || 0;
            return sum + dailyGrams;
        }, 0);

        // Get owner info from primary dog's form
        const primaryDogForm = dogs[0]?.form;
        const ownerName = primaryDogForm?.ownerName || undefined;
        const ownerEmail = primaryDogForm?.ownerEmail || undefined;

        GTM.trackResultsViewed({
            numberOfDogs: dogs.length,
            totalDailyGrams,
            ownerName,
            ownerEmail,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // 4. Track results step navigation
    const prevStepRef = useRef<number | null>(null);
    useEffect(() => {
        const stepNames: Record<number, string> = {
            1: "Summary",
            2: "Plan Selection",
            3: "Meal Selection",
            4: "Treat Selection",
            5: "Review",
        };

        GTM.trackResultsStepViewed({
            stepNumber: safeStep,
            stepName: stepNames[safeStep] || `Step ${safeStep}`,
        });

        // Meta AddToCart: meals are committed when the user advances from
        // Meal Selection (3) into Treat Selection (4). Browser-only signal —
        // forward navigation only, so back-and-forth doesn't re-fire.
        const fbq = (window as any).fbq;
        if (prevStepRef.current === 3 && safeStep === 4 && typeof fbq === "function") {
            fbq("track", "AddToCart", { content_name: "quiz_meal_selection", currency: "GBP" });
        }
        prevStepRef.current = safeStep;
    }, [safeStep]);

    const StepComponent = RESULTS_STEPS[safeStep];
    const dogCount = dogs.length;

    const meta = RESULTS_STEP_META[safeStep];
    const defaultTitle = meta?.title ?? "Results";
    const subtitleFn = meta?.subtitle;
    const defaultSubtitle = subtitleFn ? subtitleFn(dogCount) : undefined;

    // --------------------------------------------------
    // EXTREME ALLERGY CHECK (primary dog)
    // --------------------------------------------------
    const primaryDogId = activeDogId ?? dogs[0]?.id ?? null;
    // Always call hook unconditionally to satisfy React rules
    const primaryDogData = useDog(primaryDogId ?? "");
    const primaryForm = primaryDogId ? primaryDogData.form : null;

    const allAllergyValues = Object.values(Allergy);
    const selectedAllergies: string[] = Array.isArray(primaryForm?.allergies)
        ? primaryForm.allergies
        : [];

    const hasExtremeAllergies =
        allAllergyValues.length > 0 &&
        allAllergyValues.every((a) => selectedAllergies.includes(a));

    // Only block the *summary* content on step 1
    const showAllergyWarning = safeStep === 1 && hasExtremeAllergies;

    const headerTitle = showAllergyWarning ? "We’re really sorry" : defaultTitle;
    const headerSubtitle = showAllergyWarning
        ? "Based on the allergies you selected, we don’t currently have any suitable meals."
        : defaultSubtitle;

    // Screen 12 — redesigned dog profile summary under the new quiz chrome.
    // Next continues into the (unchanged) results flow at the plan step; Previous
    // returns to the owner step. The extreme-allergy case keeps the old warning UI.
    if (safeStep === 1 && !hasExtremeAllergies) {
        return (
            <ProfileSummaryScreen
                dogs={dogs}
                activeDogId={activeDogId}
                onPrev={() => {
                    dispatch({ type: "SET_GLOBAL_STEP", step: "owner" });
                    if (primaryDogId) writeQuizStepToURL("owner", primaryDogId);
                }}
                onNext={() => setResultsStep(2)}
            />
        );
    }

    // Steps 2–5 — redesigned chrome (brown nav + 4-tab progress + blue footer).
    // plan/meals/treats → "My plan" tab; review → "Summary" tab. The review step
    // gets the Figma 16v2 photo band (dog eating from bowl + 94% roundel) above
    // the tabs via the "profile" shell variant.
    if (safeStep >= 2) {
        const activeTab = safeStep === 5 ? 3 : 2;
        const isReview = safeStep === 5;
        // Choose Plan (Figma 1521:723) + Treats (Figma 1540:6518) get photo bands above the tabs.
        const isPlan = safeStep === 2;
        const isTreats = safeStep === 4;
        return (
            <>
                <QuizShell
                    activeTab={activeTab}
                    variant={isReview || isPlan || isTreats ? "profile" : "plain"}
                    heroSrc={
                        isReview
                            ? { xs: quizAsset("review-hero"), md: quizAsset("review-hero-d") }
                            : isPlan
                                ? { xs: quizAsset("plan-hero"), md: quizAsset("plan-hero-d") }
                                : isTreats
                                    ? { xs: quizAsset("treats-hero"), md: quizAsset("treats-hero-d") }
                                    : undefined
                    }
                    heroBg={isReview || isPlan || isTreats ? "#f4f1ec" : undefined}
                    heroPosition={isReview ? { xs: "center 55%", md: "center" } : isPlan ? { xs: "center 45%", md: "center" } : isTreats ? { xs: "center 30%", md: "center" } : undefined}
                    heroOverlay={isReview ? <NinetyFourBadge /> : undefined}
                    onClose={() => {
                        window.location.href = "/";
                    }}
                    contentMaxWidth={1100}
                >
                    <TurnstileProvider>
                        {StepComponent && (
                            <StepComponent dogs={dogs} activeDogId={activeDogId} />
                        )}
                    </TurnstileProvider>
                </QuizShell>
                <ResultsFooter />
            </>
        );
    }

    return (
        <Box
            sx={{
                position: "relative",
                minHeight: "100vh",
                overflow: "hidden",
                background: "linear-gradient(206deg, #E1F6FD 15%, #FFF 100%)",
                pb: "160px",
            }}
        >
            {/* Background wave */}
            <Box
                component="img"
                src={WAVE_SRC}
                alt="Wave"
                sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    zIndex: 1,
                }}
            />

            <Box
                sx={{
                    maxWidth: "1600px",
                    mx: "auto",
                }}
            >
                {/* HEADER */}
                <Box sx={{ position: "relative", zIndex: 3, pt: { xs: 3, md: 6 } }}>
                    <ResultsHeader
                        title={headerTitle}
                        subtitle={headerSubtitle}
                        logoSrc={LOGO_SRC}
                    />
                </Box>

                {/* MAIN CONTENT */}
                <Box
                    sx={{
                        position: "relative",
                        zIndex: 3,
                        width: "100%",
                        mt: { xs: 3, md: 4 },
                        px: { xs: 2, sm: 3, md: 4, lg: 6 },
                    }}
                >
                    {/* SIDEBAR (horizontal on mobile/tablet, vertical on desktop) */}
                    {!showAllergyWarning && (
                        <Box
                            sx={{
                                mb: { xs: 3, lg: 0 },
                                display: { xs: 'block', lg: 'none' },
                            }}
                        >
                            <ResultsSidebar current={safeStep} setStep={setResultsStep} />
                        </Box>
                    )}

                    <Grid
                        container
                        spacing={{ xs: 0, lg: 4 }}
                        sx={{
                            alignItems: "stretch",
                            minWidth: 0,
                            width: "100%",
                            margin: 0,
                        }}
                    >
                        {/* SIDEBAR (desktop only - sticky) */}
                        {!showAllergyWarning && (
                            <Grid
                                size={{ xs: 12, lg: 2.5 }}
                                sx={{
                                    display: { xs: 'none', lg: 'block' },
                                    minWidth: 0,

                                    // Sticky sidebar on large screens
                                    position: "sticky",
                                    top: 24,
                                    alignSelf: "flex-start",
                                }}
                            >
                                <ResultsSidebar current={safeStep} setStep={setResultsStep} />
                            </Grid>
                        )}

                        {/* STEP CONTENT */}
                        <Grid
                            size={{
                                xs: 12,
                                lg: showAllergyWarning ? 12 : 9.5
                            }}
                            sx={{
                                minWidth: 0,
                                width: "100%",
                            }}
                        >
                            {showAllergyWarning ? (
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                    <AllergyWarningCard name={primaryForm?.name} />
                                </Box>
                            ) : (
                                StepComponent && <TurnstileProvider>
                                    <StepComponent dogs={dogs} activeDogId={activeDogId} /></TurnstileProvider>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                {/* FOOTER (kept so they can go back and edit quiz answers) */}
                <ResultsFooter summaryBlocked={showAllergyWarning} />
            </Box>
        </Box>
    );
}